import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import LoadingSpinner from "../common/loading";
import AutomationEditCardContent from "./automation-edit-card-content";
import { Typography, Tabs, Tab, IconButton, Alert } from "@mhome/ui";
import { ArrowBack, ArrowForward, Refresh, Token } from "@mui/icons-material";
import { useSetRecoilState, useRecoilValue } from "recoil";
import { useEventEmitter } from "../ws/event-context";
import { currentAutomationState, wsReadyState } from "../global-state";
import { getAutomationInfo } from "../service/automation-service";
import { useIsSmallScreen } from "../common/adaptive-theme-provider";
import { useWebSocket } from "../ws/websocket";
import AutomationWidget from "../common/automation-widget";

const OverviewConfigView = () => {
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [draftScripts, setDraftScripts] = useState([]);
  const [historyScripts, setHistoryScripts] = useState([]);
  const [currentDraftIndex, setCurrentDraftIndex] = useState(0);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(0);
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);
  const [singleDraftMode, setSingleDraftMode] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [widgets, setWidgets] = useState([]);

  const history = useHistory();
  const [params, setParams] = useState({});
  const wsReady = useRecoilValue(wsReadyState);
  const { subscribe } = useEventEmitter();
  const location = useLocation();
  const setCurrentAutomation = useSetRecoilState(currentAutomationState);
  const [automationInfo, setAutomationInfo] = useState(null);
  const isSmallScreen = useIsSmallScreen();
  const { sendAndWaitWs } = useWebSocket();

  // Helper function to get current automation ID from location to avoid stale state
  const getCurrentAutomationId = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("id");
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const p = { scriptId: null };
    let hasDraftId = false;
    let hasPremadeId = false;
    let hasSharedId = false;
    let hasId = false;

    for (const [key, value] of searchParams.entries()) {
      if (key === "id") {
        hasId = true;
        p["automationId"] = value;
        if (value.startsWith("T-")) {
          p["category"] = "TEMP";
        } else if (value.startsWith("P-") || value.startsWith("S-")) {
          p["category"] = "TWIN";
        } else {
          p["category"] = "STD";
        }
      } else if (key === "draftId") {
        hasDraftId = true;
        p["scriptId"] = value;
      } else if (key === "premadeId") {
        hasPremadeId = true;
        p["premadeId"] = value;
      } else if (key === "sharedId") {
        hasSharedId = true;
        p["sharedId"] = value;
      } else {
        p[key] = value;
      }
    }

    // Determine if we're in single-draft mode
    const inSingleDraftMode =
      !hasId && (hasDraftId || hasPremadeId || hasSharedId);
    setSingleDraftMode(inSingleDraftMode);

    if (wsReady) {
      setParams(p);

      // In single-draft mode, always show draft tab (index 1)
      if (inSingleDraftMode) {
        setCurrentTab(1);
      }

      setLoading(false);
    }
  }, [wsReady, location.search]);

  // Fetch automation info and set current automation state
  const fetchAutomationInfo = async () => {
    // In single-draft mode, set draft info in current automation state
    if (singleDraftMode) {
      const draftId = params.scriptId || params.premadeId || params.sharedId;
      setCurrentAutomation({
        automationId: null,
        scriptId: draftId,
        name: `Draft(${draftId})`,
      });
      setDraftScripts([]);
      setHistoryScripts([]);
      return;
    }

    if (params.automationId) {
      // Don't set current automation for T- (TEMP) or P/S- (TWIN) categories
      if (
        params.automationId.startsWith("T-") ||
        params.automationId.startsWith("P-") ||
        params.automationId.startsWith("S-")
      ) {
        setCurrentAutomation({
          automationId: null,
          scriptId: null,
          name: null,
        });
        return;
      }

      try {
        const info = await getAutomationInfo(
          sendAndWaitWs,
          params.automationId
        );

        console.debug("info", info);

        if (info && !info.error) {
          setAutomationInfo(info);
          const automationName = info.name || "Unknown";
          setIsBlocked(info.blocked || false);

          // Set widgets if available
          if (info.widgets && Array.isArray(info.widgets)) {
            setWidgets(info.widgets);
          } else {
            setWidgets([]);
          }

          // Check if we're viewing a specific draft via hash
          const hash = location.hash;
          let currentDraftId = null;

          if (hash) {
            const hashParams = new URLSearchParams(hash.substring(1));
            currentDraftId = hashParams.get("draft");
          }

          // Set current automation with draft info if applicable
          if (currentDraftId) {
            setCurrentAutomation({
              automationId: null,
              scriptId: currentDraftId,
              name: `Draft(${currentDraftId})`,
            });
          } else {
            setCurrentAutomation({
              automationId: params.automationId,
              scriptId: null,
              name: automationName,
            });
          }

          if (info.pendingScripts) {
            const drafts = info.pendingScripts
              .filter((script) => script.type === "ASSISTANT")
              .sort((a, b) => a.createdAt - b.createdAt);

            const histories = info.pendingScripts
              .filter((script) => script.type === "HISTORY")
              .sort((a, b) => a.createdAt - b.createdAt);

            setDraftScripts(drafts);
            setHistoryScripts(histories);
          }
        } else {
          // Clear state if automation not found or error
          setCurrentAutomation({
            automationId: null,
            scriptId: null,
            name: null,
          });
          setDraftScripts([]);
          setHistoryScripts([]);
          setIsBlocked(false);
          setWidgets([]);
        }
      } catch (error) {
        console.error("Error fetching automation info:", error);
        setCurrentAutomation({
          automationId: null,
          scriptId: null,
          name: null,
        });
        setDraftScripts([]);
        setHistoryScripts([]);
        setIsBlocked(false);
        setWidgets([]);
      }
    } else {
      // Clear state if no automation ID
      setCurrentAutomation({ automationId: null, scriptId: null, name: null });
      setDraftScripts([]);
      setHistoryScripts([]);
      setIsBlocked(false);
      setWidgets([]);
    }
  };

  useEffect(() => {
    fetchAutomationInfo();
  }, [params.automationId, singleDraftMode]);

  // Listen for hash changes and update tab/index accordingly
  useEffect(() => {
    if (singleDraftMode) return; // Don't handle hash changes in single draft mode

    const hash = location.hash;
    let targetTab = 0; // Default to Active tab
    let targetScriptId = null;
    let scriptType = null;
    let foundScript = false;
    let shouldValidateExistence = false;

    if (hash) {
      const hashParams = new URLSearchParams(hash.substring(1)); // Remove # prefix
      const draftParam = hashParams.get("draft");
      const historyParam = hashParams.get("history");

      if (draftParam) {
        targetScriptId = draftParam;
        scriptType = "ASSISTANT";
        targetTab = 1;
        // Only validate existence if we have fetched the automation info and have scripts data
        shouldValidateExistence = automationInfo !== null;
        if (shouldValidateExistence) {
          foundScript = draftScripts.some(
            (script) => script.scriptId === draftParam
          );
        } else {
          foundScript = true; // Assume it exists until we can verify
        }
      } else if (historyParam) {
        targetScriptId = historyParam;
        scriptType = "HISTORY";
        targetTab = 2;
        // Only validate existence if we have fetched the automation info and have scripts data
        shouldValidateExistence = automationInfo !== null;
        if (shouldValidateExistence) {
          foundScript = historyScripts.some(
            (script) => script.scriptId === historyParam
          );
        } else {
          foundScript = true; // Assume it exists until we can verify
        }
      }
    }

    // If we have validated and the target script doesn't exist, fallback to active tab
    if (targetScriptId && shouldValidateExistence && !foundScript) {
      targetTab = 0;
      targetScriptId = null;
      scriptType = null;
      // Update URL to remove the invalid hash - use current automation ID from location
      const currentAutomationId = getCurrentAutomationId();
      if (currentAutomationId) {
        const newUrl = `/automation/overview?id=${currentAutomationId}`;
        history.replace(newUrl);
      }
    }

    // Update current tab
    setCurrentTab(targetTab);

    // Find and set the appropriate index if we have a target script ID that exists
    if (targetScriptId && scriptType && foundScript) {
      if (scriptType === "ASSISTANT" && draftScripts.length > 0) {
        const draftIndex = draftScripts.findIndex(
          (script) => script.scriptId === targetScriptId
        );
        if (draftIndex !== -1) {
          setCurrentDraftIndex(draftIndex);
        }
      } else if (scriptType === "HISTORY" && historyScripts.length > 0) {
        const historyIndex = historyScripts.findIndex(
          (script) => script.scriptId === targetScriptId
        );
        if (historyIndex !== -1) {
          setCurrentHistoryIndex(historyIndex);
        }
      }
    } else {
      // No target script or script not found, reset to first items
      setCurrentDraftIndex(0);
      setCurrentHistoryIndex(0);
    }

    // set current automation state
    let currentDraftId = null;
    const automationName = automationInfo?.name || "Unknown";
    if (hash && foundScript) {
      const hashParams = new URLSearchParams(hash.substring(1));
      currentDraftId = hashParams.get("draft");
    }
    if (currentDraftId && foundScript) {
      setCurrentAutomation({
        automationId: null,
        scriptId: currentDraftId,
        name: `Draft(${currentDraftId})`,
      });
    } else {
      setCurrentAutomation({
        automationId: params.automationId,
        scriptId: null,
        name: automationName,
      });
    }
  }, [
    location.hash,
    draftScripts,
    historyScripts,
    singleDraftMode,
    automationInfo,
  ]);

  useEffect(() => {
    if (!subscribe || !params.automationId) return;

    const { unsubscribe } = subscribe((target, data) => {
      if (target === "/automation/overview/reportUpdate") {
        const payload = JSON.parse(data.payload);
        if (payload && payload.automationId === params.automationId) {
          setShowUpdateNotification(true);
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [subscribe, params.automationId]);

  const handleRefresh = () => {
    fetchAutomationInfo();
    setShowUpdateNotification(false);
  };

  const handleAutomationSave = async (automationId) => {
    let newUrl = `/automation/overview?id=${automationId}`;
    history.replace(newUrl);
    var newParams = {
      ...params,
      automationId,
      premadeId: null,
      premadeVersion: null,
      scriptId: null,
      sharedId: null,
    };
    setParams(newParams);
  };

  const handleDelete = async (isMain, draftId, type) => {
    if (isMain) {
      if (type === "TASK_FLOW") {
        history.push("/taskflow/list");
      } else {
        history.push("/automation/list");
      }
    } else {
      // Remove the draft or archive from the list
      if (draftId) {
        // Check if it's in draft scripts
        const draftIndex = draftScripts.findIndex(
          (script) => script.scriptId === draftId
        );

        if (draftIndex !== -1) {
          // Remove from draft scripts
          const newDraftScripts = draftScripts.filter(
            (script) => script.scriptId !== draftId
          );
          setDraftScripts(newDraftScripts);

          // Adjust current draft index if necessary
          if (newDraftScripts.length === 0) {
            // No more drafts, switch to active tab
            setCurrentTab(0);
            setCurrentDraftIndex(0);
            const currentAutomationId = getCurrentAutomationId();
            if (currentAutomationId) {
              history.replace(`/automation/overview?id=${currentAutomationId}`);
            }
          } else if (currentDraftIndex >= newDraftScripts.length) {
            // Current index is out of bounds, set to last item
            const newIndex = newDraftScripts.length - 1;
            setCurrentDraftIndex(newIndex);
            // Update hash to reflect the new current script
            if (currentTab === 1) {
              const newCurrentScript = newDraftScripts[newIndex];
              if (newCurrentScript?.scriptId) {
                const currentAutomationId = getCurrentAutomationId();
                if (currentAutomationId) {
                  history.replace(
                    `/automation/overview?id=${currentAutomationId}#draft=${newCurrentScript.scriptId}`
                  );
                }
              }
            }
          }
        } else {
          // Check if it's in history scripts
          const historyIndex = historyScripts.findIndex(
            (script) => script.scriptId === draftId
          );

          if (historyIndex !== -1) {
            // Remove from history scripts
            const newHistoryScripts = historyScripts.filter(
              (script) => script.scriptId !== draftId
            );
            setHistoryScripts(newHistoryScripts);

            // Adjust current history index if necessary
            if (newHistoryScripts.length === 0) {
              // No more history items, switch to active tab
              setCurrentTab(0);
              setCurrentHistoryIndex(0);
              const currentAutomationId = getCurrentAutomationId();
              if (currentAutomationId) {
                history.replace(
                  `/automation/overview?id=${currentAutomationId}`
                );
              }
            } else if (currentHistoryIndex >= newHistoryScripts.length) {
              // Current index is out of bounds, set to last item
              const newIndex = newHistoryScripts.length - 1;
              setCurrentHistoryIndex(newIndex);
              // Update hash to reflect the new current script
              if (currentTab === 2) {
                const newCurrentScript = newHistoryScripts[newIndex];
                if (newCurrentScript?.scriptId) {
                  const currentAutomationId = getCurrentAutomationId();
                  if (currentAutomationId) {
                    history.replace(
                      `/automation/overview?id=${currentAutomationId}#history=${newCurrentScript.scriptId}`
                    );
                  }
                }
              }
            }
          }
        }
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    // Update URL based on tab selection - use current automation ID from location
    const currentAutomationId = getCurrentAutomationId();
    if (!currentAutomationId) return;

    let newUrl = `/automation/overview?id=${currentAutomationId}`;

    if (newValue === 1 && draftScripts.length > 0) {
      // Draft tab - add hash with current draft scriptId
      const currentScript = draftScripts[currentDraftIndex];
      if (currentScript?.scriptId) {
        newUrl += `#draft=${currentScript.scriptId}`;
      }
    } else if (newValue === 2 && historyScripts.length > 0) {
      // History tab - add hash with current history scriptId
      const currentScript = historyScripts[currentHistoryIndex];
      if (currentScript?.scriptId) {
        newUrl += `#history=${currentScript.scriptId}`;
      }
    }
    // Active tab (newValue === 0) - no hash needed

    history.replace(newUrl);
  };

  const handlePrevious = (tabIndex) => {
    const currentAutomationId = getCurrentAutomationId();
    if (!currentAutomationId) return;

    let newIndex;
    let newUrl = `/automation/overview?id=${currentAutomationId}`;

    if (tabIndex === 1) {
      // Draft tab
      newIndex =
        currentDraftIndex > 0 ? currentDraftIndex - 1 : draftScripts.length - 1;

      const newScript = draftScripts[newIndex];
      if (newScript?.scriptId) {
        newUrl += `#draft=${newScript.scriptId}`;
      }
    } else if (tabIndex === 2) {
      // History tab
      newIndex =
        currentHistoryIndex > 0
          ? currentHistoryIndex - 1
          : historyScripts.length - 1;

      const newScript = historyScripts[newIndex];
      if (newScript?.scriptId) {
        newUrl += `#history=${newScript.scriptId}`;
      }
    }

    history.replace(newUrl);
  };

  const handleNext = (tabIndex) => {
    const currentAutomationId = getCurrentAutomationId();
    if (!currentAutomationId) return;

    let newIndex;
    let newUrl = `/automation/overview?id=${currentAutomationId}`;

    if (tabIndex === 1) {
      // Draft tab
      newIndex =
        currentDraftIndex < draftScripts.length - 1 ? currentDraftIndex + 1 : 0;

      const newScript = draftScripts[newIndex];
      if (newScript?.scriptId) {
        newUrl += `#draft=${newScript.scriptId}`;
      }
    } else if (tabIndex === 2) {
      // History tab
      newIndex =
        currentHistoryIndex < historyScripts.length - 1
          ? currentHistoryIndex + 1
          : 0;

      const newScript = historyScripts[newIndex];
      if (newScript?.scriptId) {
        newUrl += `#history=${newScript.scriptId}`;
      }
    }

    history.replace(newUrl);
  };

  const renderTabContent = () => {
    // In single-draft mode, always show the draft content
    if (singleDraftMode) {
      return (
        <AutomationEditCardContent
          key={params.scriptId || params.premadeId || params.sharedId} // Add key to force re-mount when script changes
          params={params}
          onSave={handleAutomationSave}
          onDelete={handleDelete}
          editable={true}
        />
      );
    }

    if (currentTab === 0) {
      // Active tab - existing logic
      const isNewAutomation = !params.automationId;

      return isNewAutomation ? (
        <div
          style={{
            padding: "32px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 200,
            textAlign: "center",
          }}
        >
          <Typography
            variant="h5"
            color="textPrimary"
            className="mb-4"
            style={{
              fontWeight: 500,
            }}
          >
            Create New Automation
          </Typography>
          <div
            style={{
              padding: "16px",
              borderRadius: "16px",
              backgroundColor: "hsl(var(--background))",
            }}
          >
            <Typography
              variant="body2"
              color="textSecondary"
              style={{
                fontStyle: "italic",
              }}
            >
              Use the chat assistant to describe what automation you want to
              create
            </Typography>
          </div>
        </div>
      ) : (
        <>
          <AutomationEditCardContent
            key={params.automationId} // Add key to force re-mount when automation changes
            params={params}
            onSave={handleAutomationSave}
            onDelete={handleDelete}
            editable={true}
          />
          {/* Render widgets if available */}
          <AutomationWidget widgets={widgets} />
        </>
      );
    } else if (currentTab === 1) {
      // Draft tab
      if (draftScripts.length === 0) {
        return (
          <div
            style={{
              padding: "32px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 200,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" color="textSecondary">
              No drafts available
            </Typography>
          </div>
        );
      }

      const currentScript = draftScripts[currentDraftIndex];
      return (
        <div>
          {draftScripts.length > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              className="gap-4"
            >
              <IconButton onClick={() => handlePrevious(1)} size="small">
                <ArrowBack />
              </IconButton>
              <Typography
                color="textSecondary"
                style={{ fontSize: "0.875rem" }}
              >
                {currentDraftIndex + 1} of {draftScripts.length}
              </Typography>
              <IconButton onClick={() => handleNext(1)} size="small">
                <ArrowForward />
              </IconButton>
            </div>
          )}
          <AutomationEditCardContent
            key={currentScript.scriptId} // Add key to force re-mount when scriptId changes
            params={{
              scriptId: currentScript.scriptId,
              automationId: params.automationId,
            }}
            onSave={handleAutomationSave}
            onDelete={handleDelete}
            editable={true}
          />
        </div>
      );
    } else if (currentTab === 2) {
      // History tab
      if (historyScripts.length === 0) {
        return (
          <div
            style={{
              padding: "32px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 200,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" color="textSecondary">
              No archives available
            </Typography>
          </div>
        );
      }

      const currentScript = historyScripts[currentHistoryIndex];
      return (
        <div>
          {historyScripts.length > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              className="gap-4"
            >
              <IconButton onClick={() => handlePrevious(2)} size="small">
                <ArrowBack />
              </IconButton>
              <Typography
                color="textSecondary"
                style={{ fontSize: "0.875rem" }}
              >
                {currentHistoryIndex + 1} of {historyScripts.length}
              </Typography>
              <IconButton onClick={() => handleNext(2)} size="small">
                <ArrowForward />
              </IconButton>
            </div>
          )}
          <AutomationEditCardContent
            key={currentScript.scriptId} // Add key to force re-mount when scriptId changes
            params={{
              scriptId: currentScript.scriptId,
              automationId: params.automationId,
            }}
            onSave={handleAutomationSave}
            onDelete={handleDelete}
            editable={true}
          />
        </div>
      );
    }
  };

  const renderAutomationContent = () => {
    const isTempAutomation =
      params.automationId && params.automationId.startsWith("T-");

    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          height: "100%",
          width: isSmallScreen ? "100%" : "100%",
          maxWidth: "800px",
          padding: "8px",
          margin: "0 auto",
        }}
      >
        {showUpdateNotification && (
          <Alert
            severity="info"
            className="mb-4 w-full"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
            action={
              <IconButton
                size="small"
                onClick={handleRefresh}
                style={{ color: "hsl(var(--primary))" }}
              >
                <Refresh />
              </IconButton>
            }
          >
            The automation is updated
          </Alert>
        )}
        {isBlocked && (
          <Alert
            severity="error"
            className="mb-4 w-full"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
            icon={<Token style={{ color: "hsl(var(--destructive))" }} />}
          >
            Insufficient tokens - cannot run automation
          </Alert>
        )}
        {/* Only show tabs if not in single-draft mode and not temp automation */}
        {!singleDraftMode && !isTempAutomation && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
              marginBottom: "16px",
            }}
          >
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              aria-label="automation tabs"
              centered
              style={{
                minHeight: "auto",
              }}
            >
              <Tab label="Active" />
              <Tab label={`Draft (${draftScripts.length})`} />
              <Tab label={`Archive (${historyScripts.length})`} />
            </Tabs>
          </div>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <div style={{ padding: 0, width: "100%" }}>
            {isTempAutomation ? (
              // For temp automations, always show active content
              <AutomationEditCardContent
                key={params.automationId} // Add key to force re-mount when automation changes
                params={params}
                onSave={handleAutomationSave}
                onDelete={handleDelete}
                editable={true}
              />
            ) : (
              renderTabContent()
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        top: isSmallScreen ? "56px" : "64px",
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "transparent",
        zIndex: 1,
        padding: 0,
        position: "absolute",
      }}
    >
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div style={{ flex: 1, overflow: "hidden" }}>
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {renderAutomationContent()}
          </div>
        </div>
      )}
    </div>
  );
};

export default OverviewConfigView;
